import RadarContext, { toWmsTimeFormat } from "../contexts/RadarContext";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMap } from "react-leaflet";

import L from "leaflet";

export interface RadarLayerProps {
  opacity: number;
}

// Build load queue: current frame first, then alternating adjacent frames
const buildLoadQueue = (currentFrame: number, totalFrames: number): number[] => {
  const queue: number[] = [currentFrame];
  const added = new Set([currentFrame]);

  for (let offset = 1; offset < totalFrames; offset++) {
    const next = (currentFrame + offset) % totalFrames;
    if (!added.has(next)) {
      queue.push(next);
      added.add(next);
    }
    const prev = (currentFrame - offset + totalFrames) % totalFrames;
    if (!added.has(prev)) {
      queue.push(prev);
      added.add(prev);
    }
  }
  return queue;
};

const DEBOUNCE_MS = 400;

const RadarLayer = ({ opacity }: RadarLayerProps) => {
  const radar = useContext(RadarContext);
  const map = useMap();

  const [loadedFrames, setLoadedFrames] = useState<Set<number>>(new Set());
  const [loadingFrame, setLoadingFrame] = useState<number | null>(null);
  const [loadQueue, setLoadQueue] = useState<number[]>([]);

  // Track layers imperatively so we can remove them synchronously
  const layersRef = useRef<Map<number, L.TileLayer.WMS>>(new Map());
  const queueRef = useRef<number[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const radarRef = useRef(radar);
  const wasPlayingRef = useRef(false);
  const interactingRef = useRef(false);

  radarRef.current = radar;
  queueRef.current = loadQueue;

  // Helper functions for cleanup
  const removeAllLayers = useCallback(() => {
    layersRef.current.forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    layersRef.current.clear();
  }, [map]);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  // Use direct map.on() for synchronous event handling
  useEffect(() => {
    const onStart = () => {
      if (interactingRef.current) return;
      interactingRef.current = true;

      const currentRadar = radarRef.current;
      if (currentRadar) {
        wasPlayingRef.current = currentRadar.playing;
        if (currentRadar.playing) {
          currentRadar.setPlaying(false);
        }
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // Remove all layers synchronously
      layersRef.current.forEach((layer) => {
        if (map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
      });
      layersRef.current.clear();

      if (currentRadar) {
        currentRadar.setFramesLoaded(0);
      }
      setLoadingFrame(null);
      setLoadedFrames(new Set());
    };

    const onEnd = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const currentRadar = radarRef.current;
        if (currentRadar?.timeInfo) {
          const queue = buildLoadQueue(currentRadar.frame, currentRadar.timeInfo.times.length);
          setLoadQueue(queue);
          setLoadedFrames(new Set());
          setLoadingFrame(queue[0]);
          interactingRef.current = false;
        }
      }, DEBOUNCE_MS);
    };

    map.on("zoomstart", onStart);
    map.on("movestart", onStart);
    map.on("zoomend", onEnd);
    map.on("moveend", onEnd);

    return () => {
      map.off("zoomstart", onStart);
      map.off("movestart", onStart);
      map.off("zoomend", onEnd);
      map.off("moveend", onEnd);
    };
  }, [map]);

  // Initial load when radar data becomes available
  useEffect(() => {
    if (radar?.timeInfo && loadQueue.length === 0 && loadingFrame === null) {
      clearDebounce();
      debounceRef.current = setTimeout(() => {
        if (radar?.timeInfo) {
          const queue = buildLoadQueue(radar.frame, radar.timeInfo.times.length);
          setLoadQueue(queue);
          setLoadedFrames(new Set());
          setLoadingFrame(queue[0]);
        }
      }, DEBOUNCE_MS);
    }
    return () => {
      clearDebounce();
      removeAllLayers();
    };
  }, [radar?.timeInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage layers imperatively based on state
  useEffect(() => {
    if (!radar?.timeInfo || loadingFrame === null || interactingRef.current) {
      return;
    }

    const { wmsUrl, layer: wmsLayer, times } = radar.timeInfo;
    const { frame } = radar;

    // Determine which frames should have layers
    const framesToRender = new Set(loadedFrames);
    framesToRender.add(loadingFrame);
    framesToRender.add(frame);

    // Remove layers that shouldn't exist
    layersRef.current.forEach((layer, frameIndex) => {
      if (!framesToRender.has(frameIndex)) {
        if (map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
        layersRef.current.delete(frameIndex);
      }
    });

    // Add/update layers that should exist
    framesToRender.forEach((frameIndex) => {
      const time = times[frameIndex];
      if (!time) return;

      let layer = layersRef.current.get(frameIndex);

      if (!layer) {
        // Create new layer
        layer = L.tileLayer.wms(wmsUrl, {
          layers: wmsLayer,
          format: "image/png",
          transparent: true,
          opacity: frame === frameIndex ? opacity : 0,
          updateWhenIdle: true,
          updateWhenZooming: false,
          crossOrigin: "anonymous",
        });

        // Set time parameter
        layer.setParams({ time: toWmsTimeFormat(time) } as any);

        // Listen for load event
        layer.on("load", () => {
          setLoadedFrames((prev) => {
            const next = new Set(prev);
            next.add(frameIndex);
            return next;
          });
        });

        layer.addTo(map);
        layersRef.current.set(frameIndex, layer);
      } else {
        // Update opacity
        layer.setOpacity(frame === frameIndex ? opacity : 0);
      }
    });
  }, [radar, loadingFrame, loadedFrames, opacity, map]);

  // Track loading progress and queue next frame
  useEffect(() => {
    const currentRadar = radarRef.current;
    if (!currentRadar?.timeInfo || interactingRef.current) return;

    const loadedCount = loadedFrames.size;
    const totalFrames = currentRadar.framesTotal;
    currentRadar.setFramesLoaded(loadedCount);

    if (loadedCount === totalFrames) {
      if (wasPlayingRef.current) {
        wasPlayingRef.current = false;
        currentRadar.setPlaying(true);
      }
      return;
    }

    // Queue next unloaded frame
    const queue = queueRef.current;
    for (const frameIdx of queue) {
      if (!loadedFrames.has(frameIdx)) {
        if (loadingFrame !== frameIdx) {
          setLoadingFrame(frameIdx);
        }
        break;
      }
    }
  }, [loadedFrames, loadingFrame]);

  // Update opacity when it changes
  useEffect(() => {
    if (radar) {
      const currentLayer = layersRef.current.get(radar.frame);
      if (currentLayer) {
        currentLayer.setOpacity(opacity);
      }
    }
  }, [opacity, radar]);

  return null;
};

export default RadarLayer;
